<template>
  <div
    :class="{
      'sequence-table--dragdrop-active': isDragging,
      'sequence-table--dragdrop--disabled': disableDragDrop,
    }"
    class="sequence-table"
  >
    <table class="table-default table-default--offset-32 table">
      <thead style="background-color: #0000000a">
        <tr>
          <th></th>
          <th>Sample name</th>
          <th v-if="!isCalibrationMode">
            <span class="sequence-table__th-method"
              ><span>Method</span> <span>Method ID</span></span
            >
          </th>
          <!--          <th >-->
          <!--            <span class="sequence-table__th-method"-->
          <!--              ><span>Method</span> <span>Method ID</span></span-->
          <!--            >-->
          <!--          </th>-->
          <th
            v-if="isCalibrationMode"
            class="right"
            style="border-left: #ddd solid 2px; padding-left: 8px"
          >
            Inj. amount, μl
          </th>
          <th
            v-if="!isCromite"
            class="right"
            :style="
              !isCalibrationMode
                ? 'border-left: #ddd solid 2px; padding-left: 8px'
                : 'padding-left: 8px'
            "
          >
            Vial
          </th>
          <th style="text-align: right">Repeat</th>
          <th style="padding-right: 0">Run</th>
          <th />
        </tr>
      </thead>
      <Draggable
        v-model="tableRows"
        :animation="350"
        :disabled="disableDragDrop"
        :fallbackOnBody="false"
        :fallbackTolerance="1"
        :forceFallback="true"
        :scroll="true"
        :scrollSensitivity="100"
        :scrollSpeed="5"
        class="tbody__td--nowrap table__tbody"
        dragClass="sequence-table__dragdrop-dragging"
        draggable=".sequence-table__row--draggable"
        easing="cubic-bezier(1, 0, 0, 1)"
        ghostClass="sequence-table__dragdrop-placeholder"
        group="sequences"
        handle=".sequence-table__draggable"
        tag="tbody"
        @end="finishDrag"
        @start="startDrag"
      >
        <tr
          v-for="d in tableRows"
          :key="`${d.type}-${d.id}`"
          class="row--clickable sequence-table__row"
          :class="{
            'sequence-table__row--draggable': !draftDisableEdit(d),
          }"
        >
          <td class="td--shrink" style="padding-right: 9px; position: relative">
            <div
              class="sequence-table__draggable"
              :class="{
                'sequence-table__draggable--disabled': draftDisableEdit(d),
              }"
            >
              <IconMaterial class="sequence-table__draggable-icon" title="drag_indicator" />
            </div>
            <IconMaterial
              v-if="Boolean(d.sample) || draftDisableEdit(d)"
              :title="d.type === 'S' ? 'opacity' : 'star'"
              :size="16"
              style="padding-left: 7px"
            />
          </td>

          <td
            class="sequence-table__cell-name"
            :class="{
              'sequence-table__cell-name--calibration-mode': isCalibrationMode,
            }"
          >
            <div v-if="draftDisableEdit(d)" class="sequence-table__cell-name-content">
              <b v-if="isCurrentDraft(d.id)" class="truncate">
                {{ d.sample != null ? samples[d.sample].name : d.name }}
              </b>
              <span v-else class="truncate">{{
                d.sample != null ? samples[d.sample].name : d.name
              }}</span>
              <span v-if="getCalibrationLabel(d.sample)" class="sequence-table__concentration">{{
                getCalibrationLabel(d.sample)
              }}</span>
            </div>

            <Select
              v-else
              :itemSelected="samples[d.sample]"
              :items="samplesWithoutArchived"
              labelProp="name"
              style="width: 100%"
              :isTransparent="true"
              :hasSearch="true"
              placeholderSearch="Search sample"
              titleBottomSheet="Search sample"
              @update:itemSelected="changeSample(d, $event)"
            >
              <template #leftSideInsideItem="{ item }">
                <PackIconsEntity :entity="item.type === 'S' ? 'sample' : 'standard'" />
              </template>

              <template #rightSideInsideItem="{ item, isBottomSheet, isInDropdown }">
                <span
                  v-if="getCalibrationLabel(item.id)"
                  class="sequence-table__concentration"
                  :class="{
                    'sequence-table__concentration--selected':
                      item === samples[d.sample] && (isInDropdown || isBottomSheet),
                  }"
                  >{{ getCalibrationLabel(item.id) }}</span
                >
              </template>
            </Select>
          </td>

          <td
            v-if="!isCalibrationMode"
            class="text-gray sequence-table__cell-method"
            style="text-align: left"
          >
            <PopupInfoMethodColumn
              :hasMethodName="true"
              :method="getDraftMethod(d)"
              :isDisabled="isOpenMethodSelect || !getDraftMethod(d)"
              style="height: 32px; width: 100%; max-width: 350px"
            >
              <SelectMethod
                ref="selectMethod"
                :methodId="d.method"
                :preloadedMethod="getDraftMethod(d)"
                :isDisabled="draftDisableEdit(d)"
                :isEditable="hasPermissionToEdit"
                :configuration="configurationForMethod"
                @update:method="changeMethod(d, $event)"
                @showPopup="onMethodSelectPopupStateChanged(true)"
                @hidePopup="onMethodSelectPopupStateChanged(false)"
                @createMethod="showCreateMethodModal(d)"
                @editMethod="showEditMethodModal($event, d)"
              />
            </PopupInfoMethodColumn>
          </td>

          <td
            v-if="isCalibrationMode"
            class="td--shrink text-gray"
            style="text-align: right; border-left: #ddd solid 2px; padding-left: 8px"
          >
            <div v-if="draftDisableEdit(d)" style="width: 100%; min-width: 60px; text-align: right">
              <template v-if="isSampleInjection(d)">
                {{ formatInjectionAmount(d.injection_ul) || getInjectionAmountDefault(d) }}
              </template>
              <PopupHelper
                v-else
                text="Editing amount is only available for samples"
                placement="bottom"
              >
                <div>-</div>
              </PopupHelper>
            </div>
            <template v-else>
              <input
                v-if="isSampleInjection(d)"
                v-model="d.injection_ul"
                class="font-monospace"
                style="width: 60px; min-width: 60px; text-align: right; display: inline-block"
                type="number"
                step="1"
                :placeholder="getInjectionAmountDefault(d)"
                inputmode="numeric"
                pattern="[0-9]*"
                @focus="onInjectionAmountFocus($event, d)"
                @change="normalizeInjectionAmount(d)"
              />
              <PopupHelper
                v-else
                text="Editing volume is only available for samples"
                placement="bottom"
              >
                <div style="width: 100%; min-width: 60px; text-align: right">-</div>
              </PopupHelper>
            </template>
          </td>

          <td
            v-if="!isCromite"
            class="td--shrink text-gray right"
            :class="{
              'td--separate': !isCalibrationMode,
              'td--separate-executing':
                !isCalibrationMode && isCurrentDraft(d.id) && current.state === 'EXE',
              'td--separate-paused':
                !isCalibrationMode && isCurrentDraft(d.id) && current.state === 'PAU',
            }"
          >
            <div v-if="draftDisableEdit(d)" style="width: 40px; text-align: right">
              {{ getVialLabel(d.vial, trayConfig) }}
            </div>
            <InputVials
              v-else
              #default="{ vial, setVial, isMobile, handleBlur }"
              v-model="d.vial"
              style="display: inline-block"
              :trayConfig="trayConfig"
              placement="bottom-center"
              :isLazy="true"
              :usedVials="isCalibrationMode ? getNotAvailableVials(d) : []"
              @model="saveDraft(d)"
            >
              <input
                :value="vial"
                class="font-monospace sequence-table__input-vial"
                style="width: 40px; text-align: right"
                :disabled="isMobile"
                :class="{
                  'sequence-table__input-vial--empty': !vial,
                }"
                @input="setVial($event.target.value)"
                @blur="handleBlur"
              />
            </InputVials>
          </td>
          <td class="td--shrink text-gray" style="text-align: right">
            <div v-if="isCurrentDraft(d.id)" class="sequence-table__cell-repeat">
              <StatesForEntity
                :has-title="false"
                :hasTitleOnAdaptive="false"
                :state="current.state"
                style="margin: 0 8px 0 0; display: inline-block"
              />
              <span
                style="font-weight: bold; margin-left: auto"
                :class="{
                  'text-process': current.state === 'EXE',
                  'text-yellow': current.state === 'PAU',
                  'text-danger': current.state === '-',
                }"
              >
                {{ current.iteration }} of {{ d.repeat }}
              </span>
            </div>
            <div v-else-if="draftDisableEdit(d)" class="sequence-table__cell-repeat">
              <StatesForEntity
                v-if="d.number_of_terminated_injections !== d.repeat"
                :hasTitle="false"
                :hasTitleOnAdaptive="false"
                state="FAI"
                style="margin: 0 8px 0 0; display: inline-block"
              />
              <span class="sequence-table__run-counter"
                >{{ d.number_of_terminated_injections }} of {{ d.repeat }}</span
              >
            </div>
            <input
              v-else
              v-model.number.lazy="d.repeat"
              class="font-monospace"
              max="1000"
              min="1"
              style="width: 40px; text-align: right; display: inline-block"
              type="number"
              @change="saveDraft(d)"
            />
          </td>
          <td
            class="td--shrink text-gray"
            style="padding-right: 0; vertical-align: middle; text-align: center"
          >
            <div class="sequence-table__wrapper-checkbox">
              <label class="sequence-table__wrapper-enabled">
                <Checkbox
                  v-if="
                    isCurrentDraft(d.id) ||
                    (d.sample_injection && d.number_of_terminated_injections === d.repeat)
                  "
                  :checked="true"
                  :isDisabled="
                    isCurrentDraft(d.id) ||
                    (d.sample_injection && d.number_of_terminated_injections === d.repeat)
                  "
                />
                <Checkbox
                  v-else
                  v-model="d.enabled"
                  :isDisabled="!isValidDraft(d)"
                  @change="saveDraft(d)"
                />
              </label>
              <BtnIcon
                v-if="draftDisableEdit(d)"
                class="sequence-table__btn-switcher"
                iconMaterial="visibility"
                @click="$emit('view', d)"
              />
            </div>
            <!--            <BtnIcon-->
            <!--              v-if="draftDisableEdit(d)"-->
            <!--              class="sequence-table__btn-switcher"-->
            <!--              iconMaterial="visibility"-->
            <!--              @click="$emit('view', d)"-->
            <!--            />-->
            <!--            <label v-else class="sequence-table__wrapper-enabled">-->
            <!--              <Checkbox v-model="d.enabled" :isDisabled="!isValidDraft(d)" @change="saveDraft(d)" />-->
            <!--            </label>-->
          </td>
          <!--<td class="td&#45;&#45;shrink text-gray">-->
          <!--<button class="material-icons material-icon&#45;&#45;14 transparent" @click="deleteDraft(d)">copy</button>-->
          <!--</td>-->
          <td class="td--shrink text-gray" style="padding: 0">
            <button
              v-show="!draftDisableEdit(d) && tableRows.length > 1"
              class="material-icons material-icon--14 transparent compact sequence-table__button-delete"
              @click="deleteDraft(d)"
            >
              clear
            </button>
          </td>
        </tr>
        <tr slot="footer" class="sequence-table__row-add">
          <td
            class="td--shrink content sequence-table__cell-add"
            :colspan="(!isAllDraftsEnabled ? 3 : 7) - (isCalibrationMode ? 1 : 0)"
            @click="$emit('add')"
          >
            <IconMaterial title="add" size="16" class="sequence-table__icon-add" />
            Add a new line
          </td>
          <td v-if="!isAllDraftsEnabled" class="td--shrink td--separate" colspan="4">
            <Btn height="s" class="sequence-table__btn-select-all" @click.stop="enableAllDrafts"
              >Select all</Btn
            >
          </td>
        </tr>
      </Draggable>
    </table>

    <!-- Absolute positioned-->
    <MethodModal
      v-if="configurationForMethod != null && isShowMethodModal"
      :isOpen="true"
      :method="pMethod"
      :deviceConfiguration="configurationForMethod"
      @close="handleModalClosed"
      @create="handleMethodCreation($event)"
      @edit="handleMethodUpdate($event)"
    />
  </div>
</template>

<script>
  import StatesForEntity from '@/uikitProject/states/packs/StatesForEntity.vue';
  import Draggable from 'vuedraggable';
  import IconMaterial from '@/uikitBase/icons/IconMaterial.vue';
  import BtnIcon from '@/uikitBase/btns/BtnIcon.vue';
  import InputVials from '@/uikitProject/vials/vueInputVials/InputVials.vue';
  import { getVialLabel } from '@/utils/trayHelpers';
  import Checkbox from '@/uikitBase/switchers/Checkbox.vue';
  import Select from '@/uikitBase/selects/vueSelect/Select.vue';
  import PopupInfoMethodColumn from '@/uikitProject/popups/info/PopupInfoMethodColumn.vue';
  import PopupHelper from '@/uikitProject/popups/info/PopupHelper';
  import SelectMethod from '@/components/block/vueSequenceTable/private/SelectMethod.vue';
  import { apiMethods } from '@/api/graphql/cloud/methods';
  import { apiCalibrations } from '@/api/graphql/cloud/calibrations';
  import MethodModal from 'components/blocks/reactComponents/MethodModal';
  import Btn from '@/uikitBase/btns/Btn.vue';
  import PackIconsEntity from '@/uikitProject/icons/packs/PackIconsEntity.vue';
  import { SAMPLE_INJECTIONS_LIMIT, SEQUENCE_INJECTIONS_LIMIT } from '@/constants/samples/limits';
  import {
    evaluateRepeatLimits,
    LimitType,
  } from '@/utils/sequences/scheduledInjectionLimitsHelpers';
  import { formatPreciseNumber } from '@/utils/formatters.ts';
  import { AuthService } from '@/services/auth.service';

  const EVENT_REMOVE_DRAFT = 'removeDraft';

  export default {
    name: 'SequenceTable',

    components: {
      PackIconsEntity,
      MethodModal,
      SelectMethod,
      Btn,
      PopupInfoMethodColumn,
      PopupHelper,
      Checkbox,
      Select,
      BtnIcon,
      InputVials,
      IconMaterial,
      StatesForEntity,
      Draggable,
    },

    props: {
      device: Object,
      table: Object,
      current: Object,
      configurationForMethod: {
        type: Object,
        required: true,
      },
      samples: {
        type: Object,
        required: true,
      },
      isCalibrationMode: {
        type: Boolean,
      },
      methodDefault: Object,
    },

    data() {
      return {
        tableLocal: Object.values(this.table).sort((a, b) => a.order - b.order),
        isDragging: false,

        isOpenMethodSelect: false,

        methodsInUse: [],

        pMethod: undefined,

        draftForEditing: null,

        hasPermissionToEdit: AuthService.userData().role === 'admin',

        calibrationLevels: {},

        injectionAmountEditConfirmed: false,

        isShowMethodModal: false,
      };
    },

    computed: {
      empty() {
        return this.tableLocal != null && this.tableLocal.length > 0;
      },
      tableRows: {
        get() {
          return this.tableLocal;
        },
        set(table) {
          this.tableLocal = table;
          this.$emit('update:table', table);
        },
      },
      disableDragDrop() {
        return this.current != null && this.current.state === 'EXE';
      },

      trayConfig() {
        const { autosampler } = this.device?.configuration ?? {};
        if (autosampler?.supported_trays && autosampler?.tray != null) {
          return Object.entries(autosampler?.supported_trays).find(
            ([, id]) => id === autosampler?.tray,
          )[0];
        }

        return null;
      },

      samplesWithoutArchived() {
        return Object.values(this.samples).filter((sample) => !sample.archived);
      },

      calibrationStandardIds() {
        const ids = Object.values(this.samples ?? {})
          .filter((sample) => sample?.type === 'C' && sample.id != null)
          .map((sample) => sample.id);
        return Array.from(new Set(ids));
      },

      isAllDraftsEnabled() {
        return this.tableRows.every((draft) => draft.enabled || draft.sample_injection != null);
      },

      isCromite() {
        return this.device.model === 'cmt';
      },

      usedVials() {
        return this.tableRows.map((draft) => draft.vial);
      },
    },

    watch: {
      table(table) {
        this.tableLocal = Object.values(table).sort((a, b) => a.order - b.order);
        this.normalizeExistingInjectionAmounts();
        if (this.isCalibrationMode) {
          this.fillDefaultInjectionAmount();
        }
        this.initMethods();
        // Validate all drafts when table changes (e.g., new row added)
        this.$nextTick(() => {
          this.tableLocal.forEach((d) => {
            this.validateDraftLimits(d);
          });
        });
      },

      samplesWithoutArchived() {
        // !row.sample_injection - not started
        const shouldBeUpdated = this.tableRows.some(
          (row) =>
            !row.sample_injection &&
            row.sample &&
            !this.samplesWithoutArchived.find((sample) => sample.id === row.sample),
        );

        // To avoid an unnecessary rpc request
        if (shouldBeUpdated) {
          this.tableRows = this.tableRows.map((row) => {
            // Check if a selected sample is active for unused drafts.
            // Reset a selection if a sample was archived
            if (
              !row.sample_injection &&
              row.sample &&
              !this.samplesWithoutArchived.find((sample) => sample.id === row.sample)
            ) {
              return { ...row, sample: null };
            }

            return row;
          });
        }
      },

      tableLocal: {
        handler(drafts) {
          drafts.forEach((d) => {
            if (d.enabled && !this.isValidDraft(d)) {
              d.enabled = false;
            }
            // Validate repeat counts for injection limits
            this.validateDraftLimits(d);
          });
        },
        deep: true,
      },

      methodDefault() {
        if (this.isCalibrationMode) {
          this.fillDefaultInjectionAmount();
        }
      },

      calibrationStandardIds: {
        handler() {
          this.loadCalibrationLevels();
        },
        immediate: true,
      },
    },

    created() {
      this.normalizeExistingInjectionAmounts();
      if (this.isCalibrationMode) {
        this.fillDefaultInjectionAmount();
      }
      this.initMethods();
    },

    methods: {
      getVialLabel,
      async loadCalibrationLevels() {
        const standardIds = this.calibrationStandardIds;
        if (standardIds.length === 0) {
          this.calibrationLevels = {};
          return;
        }

        const currentLevels = this.calibrationLevels ?? {};
        const preservedLevels = Object.fromEntries(
          standardIds
            .map((id) => [id, currentLevels[id]])
            .filter(([, level]) => level !== undefined),
        );
        const missingIds = standardIds.filter((id) => preservedLevels[id] === undefined);

        let fetchedLevels = {};
        if (missingIds.length > 0) {
          const fetchedEntries = await Promise.all(
            missingIds.map(async (standardId) => {
              try {
                const level = await apiCalibrations.getCalibrationLevelByStandardId(standardId);
                return level ? [standardId, level] : null;
              } catch (error) {
                console.error('Failed to load calibration level for standard', standardId, error);
                return null;
              }
            }),
          );
          fetchedLevels = Object.fromEntries(fetchedEntries.filter(Boolean));
        }

        this.calibrationLevels = { ...preservedLevels, ...fetchedLevels };
      },
      getCalibrationLevel(sampleId) {
        if (!sampleId) {
          return null;
        }
        return this.calibrationLevels[sampleId] ?? null;
      },
      formatPreciseNumber(value) {
        return formatPreciseNumber(value);
      },
      getCalibrationLabel(sampleId) {
        const level = this.getCalibrationLevel(sampleId);
        if (!level || level.nominal_concentration == null) {
          return null;
        }

        const amount = this.formatPreciseNumber(level.nominal_concentration);
        return level.unit ? `${amount} ${level.unit}` : amount;
      },
      fillDefaultInjectionAmount() {
        if (!this.isCalibrationMode || !this.methodDefault?.injection_ul) {
          return;
        }

        const defaultValue = Math.round(this.methodDefault.injection_ul);
        this.tableLocal.forEach((d) => {
          if (!this.isSampleInjection(d)) {
            return;
          }
          if (d.injection_ul === undefined || d.injection_ul === null) {
            if (defaultValue) {
              this.$set(d, 'injection_ul', defaultValue);
            }
          }
        });
      },
      normalizeExistingInjectionAmounts() {
        this.tableLocal.forEach((draft) => {
          if (!this.isCalibrationMode) {
            draft.injection_ul = null;
            return;
          }
          if (!this.isSampleInjection(draft)) {
            draft.injection_ul = null;
            return;
          }
          draft.injection_ul = this.formatInjectionAmount(draft.injection_ul);
        });
      },
      normalizeInjectionAmount(draft) {
        if (!this.isSampleInjection(draft)) {
          draft.injection_ul = null;
          this.saveDraft(draft);
          return;
        }
        const value = parseInt(draft.injection_ul, 10);
        const normalizedValue = Number.isFinite(value) ? value : null;
        if (normalizedValue == null) {
          draft.injection_ul = null;
          this.saveDraft(draft);
          return;
        }

        draft.injection_ul = normalizedValue;
        this.saveDraft(draft);
      },
      formatInjectionAmount(value) {
        const normalized = parseInt(value, 10);
        return Number.isFinite(normalized) ? normalized : null;
      },
      validateDraftLimits(draft) {
        if (!draft || draft.repeat == null) {
          return;
        }

        const repeatNumber = Number(draft.repeat);
        if (!Number.isFinite(repeatNumber) || repeatNumber <= 0) {
          return;
        }

        const evaluation = this.getDraftLimitEvaluation(draft);
        if (evaluation.clampedRepeat < repeatNumber) {
          draft.repeat = evaluation.clampedRepeat;
        }
      },

      saveDraft(draft) {
        if (!this.isCalibrationMode) {
          draft.injection_ul = null;
        }

        const repeatNumber = Number(draft.repeat);
        if (!Number.isFinite(repeatNumber) || repeatNumber <= 0) {
          this.$emit('update:draft', draft);
          return;
        }

        const evaluation = this.getDraftLimitEvaluation(draft);
        if (evaluation.clampedRepeat < evaluation.normalizedRepeat) {
          const originalRepeats = evaluation.normalizedRepeat;
          draft.repeat = evaluation.clampedRepeat;

          if (evaluation.limitedBy === LimitType.SAMPLE) {
            this.notifyError(
              `The number of injections for a sample cannot exceed ${SAMPLE_INJECTIONS_LIMIT}. The number of repeats has been reduced from ${originalRepeats} to ${draft.repeat}. You can also select another sample.`,
              { durationSeconds: 10 },
            );
          } else if (evaluation.limitedBy === LimitType.SEQUENCE) {
            this.notifyError(
              `The total number of injections in a sequence cannot exceed ${SEQUENCE_INJECTIONS_LIMIT}. The number of repeats has been reduced from ${originalRepeats} to ${draft.repeat}.`,
              { durationSeconds: 10 },
            );
          } else if (evaluation.limitedBy === LimitType.BOTH) {
            this.notifyError(
              `Both sample limit (${SAMPLE_INJECTIONS_LIMIT}) and sequence limit (${SEQUENCE_INJECTIONS_LIMIT}) would be exceeded. The number of repeats has been reduced from ${originalRepeats} to ${draft.repeat}.`,
              { durationSeconds: 10 },
            );
          }
        }

        this.$emit('update:draft', draft);
      },

      getDraftLimitEvaluation(draft) {
        return evaluateRepeatLimits({
          rows: this.tableRows,
          sampleId: draft?.sample,
          repeat: draft?.repeat,
          excludePredicate: (row) => row === draft,
        });
      },

      deleteDraft(d) {
        this.$emit(EVENT_REMOVE_DRAFT, d);
      },

      startDrag() {
        this.isDragging = true;
      },
      finishDrag() {
        this.isDragging = false;
      },

      changeSample(draft, sample) {
        const previousType = this.isSampleInjection(draft) ? 'S' : 'C';
        draft.sample = sample?.id ?? null;
        draft.type = sample?.type ?? null;
        if (!draft.sample) {
          draft.name = null;
        }
        const nextType = this.isSampleInjection(draft) ? 'S' : 'C';
        if (previousType !== nextType) {
          draft.injection_ul = null;
        }
        if (!this.isCalibrationMode) {
          draft.injection_ul = null;
        } else if (this.isSampleInjection(draft)) {
          const methodInjection = this.getMethodInjectionValue(draft);
          draft.injection_ul = methodInjection ?? null;
        } else {
          draft.injection_ul = null;
        }
        if (this.isCalibrationMode && !this.isCromite) {
          draft.vial = null;
        }
        this.saveDraft(draft);
      },

      changeMethod(draft, method) {
        draft.method = method.id ?? null;
        if (!this.isCalibrationMode) {
          draft.injection_ul = null;
        }
        this.saveDraft(draft);
      },

      isCurrentDraft(id) {
        return !(this.current == null || id !== this.current.id);
      },

      draftDisableEdit(draft) {
        return Boolean(draft.sample_injection);
      },

      getMethodInjectionValue(draft) {
        const method = this.getDraftMethod(draft);
        if (method?.injection_ul != null) {
          return Math.round(method.injection_ul);
        }
        if (this.methodDefault?.injection_ul != null) {
          return Math.round(this.methodDefault.injection_ul);
        }
        return null;
      },

      isSampleInjection(draft) {
        if (!draft) {
          return false;
        }
        const sampleType = draft.sample ? this.samples[draft.sample]?.type : null;
        const draftType = draft.type ?? null;
        const type = sampleType ?? draftType;
        return type !== 'C';
      },

      getDraftMethod(draft) {
        return this.methodsInUse.find((m) => m.id === draft.method);
      },

      getInjectionAmountDefault(draft) {
        if (!this.isSampleInjection(draft)) {
          return undefined;
        }
        return this.getMethodInjectionValue(draft) ?? undefined;
      },

      onMethodSelectPopupStateChanged(isOpened) {
        this.isOpenMethodSelect = isOpened;
      },

      enableAllDrafts() {
        this.tableRows = this.tableRows.map((draft) => {
          const isValid = this.isValidDraft(draft);
          return {
            ...draft,
            enabled: isValid,
          };
        });
      },

      async initMethods() {
        const methodInUseIds = new Set(
          this.tableLocal.map((draft) => draft.method).filter(Boolean),
        );
        if (methodInUseIds.size > 0) {
          const hasNewMethod = [...methodInUseIds].some(
            (methodId) => !this.methodsInUse.find((method) => method.id === methodId),
          );
          if (hasNewMethod) {
            this.methodsInUse =
              (await apiMethods.getMethodsByIds([...methodInUseIds])).methods ?? [];
          }
        }
      },

      showCreateMethodModal(draft) {
        this.draftForEditing = draft;
        this.pMethod = undefined;
        this.isShowMethodModal = true;
      },
      showEditMethodModal(method, draft) {
        this.draftForEditing = draft;
        this.pMethod = method;
        this.isShowMethodModal = true;
      },

      handleMethodUpdate(method) {
        this.selectMethodForEditableDraft(method);
        this.notify('The updated method has been selected for the draft!', 15);
      },
      handleMethodCreation(method) {
        this.selectMethodForEditableDraft(method);
        this.notify('The created method has been selected for the draft!', 15);
      },

      handleModalClosed() {
        this.draftForEditing = null;
        this.isShowMethodModal = false;
      },

      selectMethodForEditableDraft(method) {
        if (this.draftForEditing) {
          this.changeMethod(this.draftForEditing, method);
        }

        this.refreshMethods();
        this.draftForEditing = null;
      },

      refreshMethods() {
        this.$store.commit('resetMethods');
        this.$refs.selectMethod.forEach((selectComponent) => {
          selectComponent.refreshMethods();
        });
      },

      isValidDraft(draft) {
        return Boolean(draft.method && draft.repeat && draft.vial);
      },

      getNotAvailableVials(draft) {
        const sampleId = draft.sample;
        const vialsUsedInSample = new Set(
          this.tableRows
            .filter((draft) => draft.sample === sampleId && Boolean(draft.vial))
            .map((draft) => draft.vial),
        );
        return this.usedVials.filter((vial) => !vialsUsedInSample.has(vial) && vial !== '');
      },

      onInjectionAmountFocus(event) {
        if (this.injectionAmountEditConfirmed) {
          return;
        }

        event.target.blur();

        this.$modal.show('dialog', {
          title: 'Edit injection amount',
          text:
            'By editing this value, you will modify the corresponding field in the method. The injection will be run with the modified method.<br><br><span style="color: #7c7c7c">Note: original method in the sequence will remain unchanged; only specific injections will use this modification.</span>',
          buttons: [
            {
              title: 'Continue',
              default: true,
              handler: () => {
                this.injectionAmountEditConfirmed = true;
                this.$modal.hide('dialog');
                this.$nextTick(() => {
                  event.target.focus();
                });
              },
              class: 'vue-dialog-button red-text',
            },
            {
              title: 'Cancel',
              handler: () => {
                this.$modal.hide('dialog');
              },
              class: 'vue-dialog-button blue-text',
            },
          ],
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .sequence-table {
    overflow-x: auto;
    overflow-y: hidden;

    @screen sm {
      overflow-x: initial;
      overflow-y: initial;
    }

    &__btn-switcher {
      height: 29px;
      width: 29px;
      font-size: 15px;
    }

    &__wrapper-enabled {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 29px;
      height: 29px;
    }

    &__wrapper-checkbox {
      display: flex;
      align-items: center;
    }

    &__button-delete {
      opacity: 0;
      height: 100%;
      width: 32px;
      box-sizing: border-box;
    }

    &__row:hover &__button-delete {
      opacity: 1;
    }

    &__method-default-link {
      font-style: italic;
      opacity: 0;
    }

    &__row:hover &__method-default-link {
      opacity: 1;
    }

    &__row {
      position: relative;
    }

    &__cell-add {
      cursor: pointer;

      &:hover {
        color: $color-text-primary--hover;
        background-color: $color-bg-transparent--hover;
      }
      &:active {
        color: $color-text-primary--active;
      }
    }

    &__icon-add {
      margin-left: 7px;
      margin-right: 10px;
    }

    &:not(&--dragdrop-active):not(&--dragdrop--disabled)
      &__row:hover
      &__draggable:not(&__draggable--disabled) {
      opacity: 1;
    }

    &--dragdrop-active {
      user-select: none;

      * {
        cursor: grab;
      }
    }

    &--dragdrop-active &__row:hover {
      &.row--clickable {
        background-color: transparent;
      }

      &.row--clickable:nth-child(even) {
        background-color: $color-bg-transparent--nth;
      }
    }

    &__row:active.row--clickable:nth-child(even) {
      background-color: $color-bg-transparent--hover;
    }

    &__draggable {
      position: absolute;
      top: 50%;
      left: 0;
      transform: translateY(-50%);
      padding-left: 4px;
      height: 29px;
      width: 30px;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 15px;
      opacity: 0;
      cursor: grab;
      transition: opacity 0.1s;
    }

    &__dragdrop-placeholder {
      * {
        cursor: grab;
      }

      opacity: 0;
    }

    &__dragdrop-dragging {
      opacity: 1 !important;
      background-color: $color-bg-second !important;

      td:nth-last-of-type(2),
      td:nth-last-of-type(4) {
        padding-right: 20px !important;
      }
    }

    &__cell-name {
      max-width: 150px;
      width: 50%;
      overflow: hidden;
      text-overflow: ellipsis;

      &--calibration-mode {
        width: 80%;
      }
    }

    &__cell-name-content {
      display: flex;
      width: 100%;
      justify-content: space-between;
      padding-right: 20px;
      gap: 5px;
    }

    &__concentration {
      color: $color-text-second;

      &--selected {
        color: $pure-color__white;
      }
    }

    &__cell-method {
      min-width: 250px;
    }

    &__cell-repeat {
      display: flex;
      justify-content: space-between;
    }

    &__run-counter {
      margin-left: auto;
    }

    &__th-method {
      display: flex;
      width: 100%;
      justify-content: space-between;
    }

    &__input-vial {
      margin-left: 5px;
      padding: 0 2px !important;
      text-indent: 0 !important;

      &--empty {
        background-color: #ffd0d0 !important;
      }
    }

    &__btn-select-all {
      margin-left: 5px;
    }
  }

  .table input,
  .table select {
    border: 0 solid transparent;
    border-bottom-width: 2px;
    background: transparent;
    border-radius: 3px;
    padding: 6px 0 0 0;
    text-indent: 0;
    font-size: 13px;
  }

  .table select {
    padding-top: 1px;
  }

  .table input {
    padding: 6px 0 0 0;
  }

  .table__tbody td {
    font-size: 13px;
    font-weight: normal;
    color: #000;
    white-space: nowrap;
    padding-top: 0;
    padding-bottom: 0;
  }

  .table__tbody tr {
    height: 32px;
  }

  .table__tbody input {
    padding-bottom: 4px;
    height: 24px;
  }

  .table__tbody tr input::placeholder {
    color: #00000000;
  }

  .table__tbody tr:hover input::placeholder {
    color: #00000066;
  }

  .table input[type='number']::-webkit-inner-spin-button,
  .table input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .td--separate {
    border-left: #ddd solid 2px;
    text-align: right;

    &-executing {
      border-left: $color-text-process solid 2px;
      text-align: right;
    }

    &-paused {
      border-left: $pure-color__yellow solid 2px;
      text-align: right;
    }
  }
</style>
